import React, { useCallback, useState } from 'react';
import { useForm } from 'react-final-form';
import classNames from 'classnames';
import { Autocomplete, Box, GoogleColorLogo, makeStyles, Paper, Popper, PopperProps, Typography } from 'housecall-ui';

import { AutocompletePrediction, getPlaceDetails, getPredictions } from '../../api/googlePlaces';
import { FormField } from '../../components/form/FormField';
import { ContactDetailsValues } from '../../state/types';
import { mapGoogleObjectToAddress } from '../../utils/mapGoogleObjectToAddress';

import { AutoCompleteRenderOption } from './AutoCompleteRenderOption';

const useStyles = makeStyles(() => ({
  popper: {
    width: 'fit-content !important',
  },
  listbox: {
    padding: 0,
  },
}));

const AutoCompletePopper = (props: PopperProps) => {
  const { popper } = useStyles();
  return <Popper {...props} className={classNames(props.className, popper)} placement="bottom-start" />;
};

type FieldName = keyof ContactDetailsValues;

export const OnlineBookingAutoComplete = () => {
  const formApi = useForm();
  const formState = formApi.getState();
  const [isOpen, setOpen] = useState<boolean>(false);
  const [addressQuery, setAddressQuery] = useState<string>(formState.values.addressLine1 || '');
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const { listbox } = useStyles();

  const fetchPredictions = useCallback(async () => {
    if (!addressQuery) {
      return;
    }
    const newPredictions = await getPredictions(addressQuery);
    setPredictions(newPredictions);
  }, [addressQuery]);

  const onQueryChange = (_: unknown, value: string, reason: string) => {
    if (reason === 'input') {
      setAddressQuery(value);
      formApi.change('addressLine1', value);
      fetchPredictions();
    }
  };

  const onAddressSelect = async (_: unknown, place: NonNullable<string | AutocompletePrediction>) => {
    if (!place || typeof place === 'string') {
      return;
    }

    const placeDetails = await getPlaceDetails(place);

    if (!placeDetails) {
      return;
    }

    const addressData = mapGoogleObjectToAddress(placeDetails);
    setAddressQuery(addressData.street);

    if (addressData.city) {
      formApi.change('city', addressData.city);
    }

    if (addressData.street) {
      formApi.change('addressLine1', addressData.street);
    }
    if (addressData.streetLine2) {
      formApi.change('addressLine2', addressData.streetLine2);
    }
    if (addressData.state) {
      formApi.change('state', addressData.state);
    }
    if (addressData.postalCode) {
      formApi.change('zipCode', addressData.postalCode);
    }
    formApi.change('country', placeDetails.country === 'US' ? 'United States' : 'Canada');
  };

  return (
    <Autocomplete
      autoComplete={false}
      open={isOpen}
      PaperComponent={({ children, ...props }) => (
        <Paper {...props} elevation={1}>
          {children}
          {predictions.length > 0 && (
            <Box ml={2.5} mb={0.5} display="flex" alignItems="center">
              <Box mr={0.5}>
                <Typography variant="caption">Powered by </Typography>
              </Box>
              <GoogleColorLogo width={50} />
            </Box>
          )}
        </Paper>
      )}
      onOpen={() => setOpen(true)}
      onFocus={fetchPredictions}
      onBlur={() => setOpen(false)}
      onClose={() => setOpen(false)}
      PopperComponent={AutoCompletePopper}
      freeSolo
      onChange={onAddressSelect}
      selectOnFocus
      clearOnBlur
      openOnFocus
      handleHomeEndKeys
      disableClearable
      options={predictions}
      onInputChange={onQueryChange}
      classes={{ listbox }}
      inputValue={addressQuery}
      getOptionLabel={(option: AutocompletePrediction) => option.description}
      filterOptions={(options) => options}
      renderOption={(option: AutocompletePrediction) => <AutoCompleteRenderOption key={option.place_id} {...option} />}
      renderInput={(params) => (
        <FormField<FieldName>
          data-testid="online-booking-contact-street"
          name="addressLine1"
          {...params}
          variant="filled"
          autoComplete="address-line1"
          fullWidth
          label="Street address *"
          inputProps={{
            ...params.inputProps,
            autoComplete: 'new-password',
          }}
        />
      )}
    />
  );
};
